import type { Member } from './members.js';

/** The Realtime channel every signed-in member joins to show they're online. */
export const PRESENCE_CHANNEL = 'studio';

/** What each client tracks on the presence channel. */
export interface StudioPresence {
  member_id: string;
  display_name: string;
  avatar_url: string | null;
  /** ISO timestamp of when this client came online. */
  online_at: string;
}

export function presencePayload(
  member: Pick<Member, 'id' | 'display_name' | 'avatar_url'>,
  now = new Date(),
): StudioPresence {
  return {
    member_id: member.id,
    display_name: member.display_name,
    avatar_url: member.avatar_url,
    online_at: now.toISOString(),
  };
}

/**
 * Flattens a Realtime presence state into one entry per member, so a founder with
 * the desktop app and a browser tab open shows once, as of their earliest client.
 * Sorted by name.
 */
export function onlineMembers(state: Record<string, readonly Partial<StudioPresence>[]>): StudioPresence[] {
  const byMember = new Map<string, StudioPresence>();
  for (const entries of Object.values(state)) {
    for (const entry of entries) {
      if (!entry.member_id || !entry.display_name || !entry.online_at) continue;
      const existing = byMember.get(entry.member_id);
      if (!existing || entry.online_at < existing.online_at) {
        byMember.set(entry.member_id, {
          member_id: entry.member_id,
          display_name: entry.display_name,
          avatar_url: entry.avatar_url ?? null,
          online_at: entry.online_at,
        });
      }
    }
  }
  return [...byMember.values()].sort((a, b) => a.display_name.localeCompare(b.display_name));
}
